import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  ClickAwayListener,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type HotelChainOption, useHotelChainSelector } from './useHotelChainSelector.js';

/** Mobile hotel chain single-select (Popper) for the header. Spazious-admin only. */
export function HotelChainSelectorMobile() {
  const intl = useIntl();

  const {
    isSpaziousAdmin,
    chainOptions,
    value,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading,
  } = useHotelChainSelector();

  const [open, setOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (listboxRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (listboxRef.current) listboxRef.current.scrollTop = scrollPositionRef.current;
      });
    }
  }, [chainOptions]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
  }, [setInputValue]);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      handleInputChange(e.target.value);
    },
    [handleInputChange, setInputValue]
  );

  const selectChainOption = useCallback(
    (option: HotelChainOption) => {
      void handleChange(option);
      setOpen(false);
    },
    [handleChange]
  );

  const handleToggle = useCallback(() => {
    if (!open) {
      setInputValue('');
      handleInputChange(undefined);
    }
    setOpen((prev) => !prev);
  }, [open, handleInputChange, setInputValue]);

  const handleListboxScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const listbox = e.currentTarget;
      if (!listbox) return;
      scrollPositionRef.current = listbox.scrollTop;
      const atBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 8;
      if (atBottom && shouldLoadMore && !isLoading) handleLoadMore();
    },
    [shouldLoadMore, handleLoadMore, isLoading]
  );

  if (!isSpaziousAdmin) return null;

  const label = intl.formatMessage({
    id: 'HEADER.HOTEL_CHAIN_SELECTOR.LABEL',
    defaultMessage: 'Hotel chain',
  });
  const searchPlaceholder = intl.formatMessage({
    id: 'HEADER.HOTEL_CHAIN_SELECTOR.SEARCH',
    defaultMessage: 'Search hotel chains...',
  });
  const emptyText = intl.formatMessage({
    id: 'HEADER.HOTEL_CHAIN_SELECTOR.EMPTY',
    defaultMessage: 'No hotel chains found',
  });

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Stack alignItems="center" direction="row" sx={{ position: 'relative' }}>
        <Button
          ref={toggleButtonRef}
          data-testid="mobile-hotel-chain-selector-button"
          size="small"
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            whiteSpace: 'nowrap',
            fontSize: '0.7rem',
            px: 0.75,
            minWidth: 84,
            flexShrink: 0,
          }}
          variant="outlined"
          onClick={handleToggle}
        >
          {label}
        </Button>

        <Popper
          anchorEl={toggleButtonRef.current}
          modifiers={[{ name: 'flip', enabled: false }]}
          open={open}
          placement="bottom-start"
          style={{ zIndex: 1300, width: 280 }}
        >
          <Card>
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                autoFocus
                fullWidth
                placeholder={searchPlaceholder}
                size="small"
                value={inputValue}
                onChange={handleSearchInputChange}
              />
            </Box>

            <Box
              ref={listboxRef}
              sx={{ maxHeight: 200, overflowY: 'auto' }}
              onScroll={handleListboxScroll}
            >
              {chainOptions.length === 0 ? (
                <Box
                  sx={{
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {isLoading ? <CircularProgress size={20} /> : emptyText}
                </Box>
              ) : (
                chainOptions.map((option) => {
                  const isSelected = value?.value === option.value;
                  return (
                    <Box
                      key={option.value}
                      sx={{
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => selectChainOption(option)}
                    >
                      <Typography noWrap variant="body2">
                        {option.label}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {isLoading && chainOptions.length > 0 && (
                <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </Box>
          </Card>
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
